import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      about: 'About Us',
      services: 'Services',
      recruitment: 'Recruitment',
      training: 'Training',
      consulting: 'Consulting',
      careerAdvisory: 'Career Advisory',
      jobs: 'Jobs',
      courses: 'Courses',
      insights: 'Insights',
      contact: 'Contact',
      login: 'Login',
      register: 'Register',
      dashboard: 'Dashboard',
      logout: 'Logout',
    },
    // Hero Section
    hero: {
      title: 'Your Strategic Partner in',
      titleHighlight: 'Human Resources',
      subtitle: 'Empowering organizations across Europe, the Middle East, and beyond with premium recruitment, training, and HR consulting services.',
      cta1: 'Request Recruitment Support',
      cta2: 'Explore Training Courses',
    },
    // Services
    services: {
      title: 'Our Services',
      subtitle: 'Comprehensive HR solutions tailored to your needs',
      recruitment: {
        title: 'Recruitment Services',
        description: 'Find the right talent for your organization with our expert recruitment solutions across all industries and levels.',
        cta: 'Request Recruitment Support',
      },
      training: {
        title: 'Training Services',
        description: 'Develop your workforce with our comprehensive training programs delivered online or on-site.',
        cta: 'Explore Courses',
      },
      consulting: {
        title: 'HR Consulting',
        description: 'Transform your HR operations with strategic consulting, policies, and systems implementation.',
        cta: 'Learn More',
      },
      career: {
        title: 'Career Advisory',
        description: 'Advance your career with professional CV support, interview coaching, and career transition guidance.',
        cta: 'Get Career Support',
      },
    },
    // Why WeDo
    whyWedo: {
      title: 'Why Choose WeDo?',
      subtitle: 'We bring international expertise with local understanding',
      points: {
        expertise: {
          title: 'Industry Expertise',
          description: 'Deep knowledge across sectors including technology, finance, healthcare, and manufacturing.',
        },
        global: {
          title: 'Global Reach',
          description: 'Serving clients across Europe, Middle East, and international markets from our base in Germany.',
        },
        quality: {
          title: 'Quality Assured',
          description: 'Accredited training programs and proven recruitment methodologies that deliver results.',
        },
        bilingual: {
          title: 'Bilingual Services',
          description: 'Full support in English and Arabic to serve our diverse international clientele.',
        },
      },
    },
    // Training
    training: {
      featured: 'Featured Courses',
      viewAll: 'View All Courses',
      duration: 'Duration',
      days: 'days',
      hours: 'hours',
      mode: 'Delivery',
      language: 'Language',
      price: 'Price',
      requestQuote: 'Request Quote',
      enroll: 'Enroll Now',
      online: 'Online',
      onsite: 'On-site',
      hybrid: 'Hybrid',
      english: 'English',
      arabic: 'Arabic',
      bilingual: 'Bilingual',
      objectives: 'Learning Objectives',
      outline: 'Course Outline',
      whoShouldAttend: 'Who Should Attend',
      customTraining: 'Need Customized Training?',
      customTrainingDesc: 'We design and deliver tailored training programs for organizations.',
      requestCustom: 'Request Customized Course',
    },
    // Jobs
    jobs: {
      title: 'Open Positions',
      subtitle: 'Find your next career opportunity',
      search: 'Search jobs...',
      filter: 'Filter',
      allTypes: 'All Types',
      allModels: 'All Work Models',
      allCountries: 'All Countries',
      fullTime: 'Full-time',
      partTime: 'Part-time',
      contract: 'Contract',
      internship: 'Internship',
      onsite: 'On-site',
      hybrid: 'Hybrid',
      remote: 'Remote',
      apply: 'Apply Now',
      viewDetails: 'View Details',
      requirements: 'Requirements',
      benefits: 'Benefits',
      skills: 'Skills',
      experience: 'Experience',
      years: 'years',
      salary: 'Salary Range',
      noJobs: 'No jobs found matching your criteria.',
      talentPool: 'Join Our Talent Pool',
      talentPoolDesc: "Don't see a matching position? Submit your profile to our talent pool.",
    },
    // Forms
    forms: {
      fullName: 'Full Name',
      email: 'Email Address',
      phone: 'Phone Number',
      country: 'Country',
      city: 'City',
      company: 'Company',
      position: 'Position',
      currentPosition: 'Current Position',
      currentCompany: 'Current Company',
      department: 'Department',
      location: 'Location',
      employmentType: 'Employment Type',
      workModel: 'Work Model',
      salaryRange: 'Salary Range',
      salaryMin: 'Minimum Salary',
      salaryMax: 'Maximum Salary',
      urgency: 'Urgency',
      urgent: 'Urgent',
      normal: 'Normal',
      low: 'Low Priority',
      jobDescription: 'Job Description',
      requirements: 'Requirements',
      notes: 'Additional Notes',
      submit: 'Submit',
      sending: 'Sending...',
      success: 'Submitted successfully!',
      error: 'An error occurred. Please try again.',
      linkedin: 'LinkedIn Profile',
      cv: 'Upload CV',
      portfolio: 'Portfolio URL',
      skills: 'Skills (comma separated)',
      languages: 'Languages',
      experienceYears: 'Years of Experience',
      desiredSalary: 'Desired Salary',
      noticePeriod: 'Notice Period',
      gdprConsent: 'I consent to the processing of my personal data in accordance with the Privacy Policy and GDPR regulations.',
      organization: 'Organization Name',
      contactPerson: 'Contact Person',
      industry: 'Industry',
      topics: 'Training Topics',
      audienceSize: 'Number of Participants',
      preferredDates: 'Preferred Dates',
      preferredLocation: 'Preferred Location',
      trainingGoals: 'Training Goals',
      budget: 'Budget Range',
      subject: 'Subject',
      message: 'Message',
      inquiryType: 'Inquiry Type',
      general: 'General Inquiry',
    },
    // Contact
    contact: {
      title: 'Get in Touch',
      subtitle: 'We would love to hear from you',
      address: 'Address',
      phone: 'Phone',
      email: 'Email',
      hours: 'Business Hours',
      hoursValue: 'Monday - Friday: 9:00 AM - 6:00 PM CET',
      whatsapp: 'Chat on WhatsApp',
    },
    // Footer
    footer: {
      description: 'Your strategic partner in human resources, delivering premium recruitment, training, and consulting services across Europe and the Middle East.',
      quickLinks: 'Quick Links',
      services: 'Services',
      legal: 'Legal',
      privacy: 'Privacy Policy',
      terms: 'Terms & Conditions',
      cookies: 'Cookie Policy',
      copyright: '© 2024 WeDo HR Services UG. All rights reserved.',
      germany: 'Based in Germany',
    },
    // About
    about: {
      title: 'About WeDo HR Services',
      subtitle: 'Building bridges between talent and opportunity',
      overview: 'Company Overview',
      overviewText: 'WeDo HR Services UG is a Germany-based human resources company serving organizations and individuals across Europe, the Middle East, and beyond. We specialize in recruitment services, corporate training, HR consulting, and career advisory.',
      mission: 'Our Mission',
      missionText: 'To empower organizations with the talent and skills they need to thrive, while helping individuals achieve their career aspirations through professional development and strategic guidance.',
      vision: 'Our Vision',
      visionText: 'To be the leading HR partner of choice for international businesses seeking quality talent solutions and world-class training programs.',
      values: 'Our Values',
      valuesList: {
        excellence: { title: 'Excellence', description: 'Delivering the highest quality in everything we do' },
        integrity: { title: 'Integrity', description: 'Building trust through honest and ethical practices' },
        partnership: { title: 'Partnership', description: 'Working closely with clients to achieve shared success' },
        innovation: { title: 'Innovation', description: 'Embracing new approaches to solve complex challenges' },
      },
    },
    // CTA Sections
    cta: {
      recruitment: {
        title: 'Looking to Hire?',
        subtitle: 'Let us find the perfect candidates for your team.',
        button: 'Request Recruitment Support',
      },
      training: {
        title: 'Need Corporate Training?',
        subtitle: 'Customized programs designed for your organization.',
        button: 'Request Customized Training',
      },
      jobs: {
        title: 'Ready for Your Next Career Move?',
        subtitle: 'Explore opportunities with leading companies.',
        button: 'Browse Open Positions',
      },
    },
    // Admin
    admin: {
      dashboard: 'Dashboard',
      jobs: 'Jobs',
      courses: 'Courses',
      categories: 'Categories',
      applications: 'Applications',
      recruitmentRequests: 'Recruitment Requests',
      trainingRequests: 'Training Requests',
      inquiries: 'Inquiries',
      talentPool: 'Talent Pool',
      users: 'Users',
      settings: 'Settings',
      overview: 'Overview',
      addNew: 'Add New',
      edit: 'Edit',
      delete: 'Delete',
      save: 'Save',
      cancel: 'Cancel',
      status: 'Status',
      date: 'Date',
      actions: 'Actions',
      search: 'Search...',
      noData: 'No data found',
      confirmDelete: 'Are you sure you want to delete this item?',
    },
    // Common
    common: {
      loading: 'Loading...',
      error: 'Something went wrong',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      learnMore: 'Learn More',
      getStarted: 'Get Started',
      readMore: 'Read More',
      seeAll: 'See All',
      close: 'Close',
    },
  },
  ar: {
    // Navigation
    nav: {
      home: 'الرئيسية',
      about: 'من نحن',
      services: 'خدماتنا',
      recruitment: 'التوظيف',
      training: 'التدريب',
      consulting: 'الاستشارات',
      careerAdvisory: 'الإرشاد المهني',
      jobs: 'الوظائف',
      courses: 'الدورات',
      insights: 'رؤى ويدو',
      contact: 'اتصل بنا',
      login: 'تسجيل الدخول',
      register: 'إنشاء حساب',
      dashboard: 'لوحة التحكم',
      logout: 'تسجيل الخروج',
    },
    // Hero Section
    hero: {
      title: 'شريككم الاستراتيجي في',
      titleHighlight: 'الموارد البشرية',
      subtitle: 'نمكّن المؤسسات في أوروبا والشرق الأوسط وما وراءها من خلال خدمات التوظيف والتدريب والاستشارات المتميزة.',
      cta1: 'طلب دعم التوظيف',
      cta2: 'استكشف الدورات التدريبية',
    },
    // Services
    services: {
      title: 'خدماتنا',
      subtitle: 'حلول موارد بشرية شاملة مصممة لتلبية احتياجاتكم',
      recruitment: {
        title: 'خدمات التوظيف',
        description: 'اعثر على المواهب المناسبة لمؤسستك من خلال حلول التوظيف المتخصصة لدينا في جميع الصناعات والمستويات.',
        cta: 'طلب دعم التوظيف',
      },
      training: {
        title: 'خدمات التدريب',
        description: 'طوّر قدرات فريقك من خلال برامجنا التدريبية الشاملة المقدمة عبر الإنترنت أو في الموقع.',
        cta: 'استكشف الدورات',
      },
      consulting: {
        title: 'استشارات الموارد البشرية',
        description: 'حوّل عمليات الموارد البشرية لديك من خلال الاستشارات الاستراتيجية والسياسات وتطبيق الأنظمة.',
        cta: 'اعرف المزيد',
      },
      career: {
        title: 'الإرشاد المهني',
        description: 'طوّر مسيرتك المهنية من خلال دعم السيرة الذاتية والتحضير للمقابلات وتوجيه الانتقال الوظيفي.',
        cta: 'احصل على الدعم المهني',
      },
    },
    // Why WeDo
    whyWedo: {
      title: 'لماذا تختار ويدو؟',
      subtitle: 'نجمع بين الخبرة الدولية والفهم المحلي',
      points: {
        expertise: {
          title: 'خبرة صناعية',
          description: 'معرفة عميقة عبر القطاعات بما في ذلك التكنولوجيا والمالية والرعاية الصحية والتصنيع.',
        },
        global: {
          title: 'انتشار عالمي',
          description: 'نخدم العملاء في أوروبا والشرق الأوسط والأسواق الدولية من مقرنا في ألمانيا.',
        },
        quality: {
          title: 'جودة مضمونة',
          description: 'برامج تدريبية معتمدة ومنهجيات توظيف مثبتة تحقق النتائج.',
        },
        bilingual: {
          title: 'خدمات ثنائية اللغة',
          description: 'دعم كامل باللغتين الإنجليزية والعربية لخدمة عملائنا الدوليين.',
        },
      },
    },
    // Training
    training: {
      featured: 'الدورات المميزة',
      viewAll: 'عرض جميع الدورات',
      duration: 'المدة',
      days: 'أيام',
      hours: 'ساعات',
      mode: 'طريقة التقديم',
      language: 'اللغة',
      price: 'السعر',
      requestQuote: 'طلب عرض سعر',
      enroll: 'سجل الآن',
      online: 'عبر الإنترنت',
      onsite: 'حضوري',
      hybrid: 'هجين',
      english: 'الإنجليزية',
      arabic: 'العربية',
      bilingual: 'ثنائي اللغة',
      objectives: 'أهداف التعلم',
      outline: 'محتوى الدورة',
      whoShouldAttend: 'من يجب أن يحضر',
      customTraining: 'هل تحتاج تدريباً مخصصاً؟',
      customTrainingDesc: 'نصمم وننفذ برامج تدريبية مخصصة للمؤسسات.',
      requestCustom: 'طلب دورة مخصصة',
    },
    // Jobs
    jobs: {
      title: 'الوظائف المتاحة',
      subtitle: 'اعثر على فرصتك المهنية التالية',
      search: 'ابحث عن وظائف...',
      filter: 'تصفية',
      allTypes: 'جميع الأنواع',
      allModels: 'جميع نماذج العمل',
      allCountries: 'جميع الدول',
      fullTime: 'دوام كامل',
      partTime: 'دوام جزئي',
      contract: 'عقد',
      internship: 'تدريب',
      onsite: 'في الموقع',
      hybrid: 'هجين',
      remote: 'عن بعد',
      apply: 'قدم الآن',
      viewDetails: 'عرض التفاصيل',
      requirements: 'المتطلبات',
      benefits: 'المزايا',
      skills: 'المهارات',
      experience: 'الخبرة',
      years: 'سنوات',
      salary: 'نطاق الراتب',
      noJobs: 'لم يتم العثور على وظائف تطابق معاييرك.',
      talentPool: 'انضم إلى مجمع المواهب',
      talentPoolDesc: 'لم تجد وظيفة مناسبة؟ أرسل ملفك الشخصي إلى مجمع المواهب لدينا.',
    },
    // Forms
    forms: {
      fullName: 'الاسم الكامل',
      email: 'البريد الإلكتروني',
      phone: 'رقم الهاتف',
      country: 'الدولة',
      city: 'المدينة',
      company: 'الشركة',
      position: 'المنصب',
      currentPosition: 'المنصب الحالي',
      currentCompany: 'الشركة الحالية',
      department: 'القسم',
      location: 'الموقع',
      employmentType: 'نوع التوظيف',
      workModel: 'نموذج العمل',
      salaryRange: 'نطاق الراتب',
      salaryMin: 'الحد الأدنى للراتب',
      salaryMax: 'الحد الأقصى للراتب',
      urgency: 'الاستعجال',
      urgent: 'عاجل',
      normal: 'عادي',
      low: 'أولوية منخفضة',
      jobDescription: 'وصف الوظيفة',
      requirements: 'المتطلبات',
      notes: 'ملاحظات إضافية',
      submit: 'إرسال',
      sending: 'جاري الإرسال...',
      success: 'تم الإرسال بنجاح!',
      error: 'حدث خطأ. يرجى المحاولة مرة أخرى.',
      linkedin: 'ملف LinkedIn',
      cv: 'تحميل السيرة الذاتية',
      portfolio: 'رابط الأعمال',
      skills: 'المهارات (مفصولة بفواصل)',
      languages: 'اللغات',
      experienceYears: 'سنوات الخبرة',
      desiredSalary: 'الراتب المطلوب',
      noticePeriod: 'فترة الإشعار',
      gdprConsent: 'أوافق على معالجة بياناتي الشخصية وفقاً لسياسة الخصوصية ولوائح حماية البيانات.',
      organization: 'اسم المؤسسة',
      contactPerson: 'جهة الاتصال',
      industry: 'الصناعة',
      topics: 'مواضيع التدريب',
      audienceSize: 'عدد المشاركين',
      preferredDates: 'التواريخ المفضلة',
      preferredLocation: 'الموقع المفضل',
      trainingGoals: 'أهداف التدريب',
      budget: 'نطاق الميزانية',
      subject: 'الموضوع',
      message: 'الرسالة',
      inquiryType: 'نوع الاستفسار',
      general: 'استفسار عام',
    },
    // Contact
    contact: {
      title: 'تواصل معنا',
      subtitle: 'يسعدنا سماعك',
      address: 'العنوان',
      phone: 'الهاتف',
      email: 'البريد الإلكتروني',
      hours: 'ساعات العمل',
      hoursValue: 'الاثنين - الجمعة: 9:00 صباحاً - 6:00 مساءً بتوقيت وسط أوروبا',
      whatsapp: 'تواصل عبر واتساب',
    },
    // Footer
    footer: {
      description: 'شريككم الاستراتيجي في الموارد البشرية، نقدم خدمات التوظيف والتدريب والاستشارات المتميزة في أوروبا والشرق الأوسط.',
      quickLinks: 'روابط سريعة',
      services: 'الخدمات',
      legal: 'قانوني',
      privacy: 'سياسة الخصوصية',
      terms: 'الشروط والأحكام',
      cookies: 'سياسة ملفات تعريف الارتباط',
      copyright: '© 2024 ويدو لخدمات الموارد البشرية. جميع الحقوق محفوظة.',
      germany: 'مقرنا في ألمانيا',
    },
    // About
    about: {
      title: 'عن ويدو لخدمات الموارد البشرية',
      subtitle: 'بناء جسور بين المواهب والفرص',
      overview: 'نبذة عن الشركة',
      overviewText: 'ويدو لخدمات الموارد البشرية هي شركة ألمانية تخدم المؤسسات والأفراد في أوروبا والشرق الأوسط وما وراءها. نتخصص في خدمات التوظيف والتدريب المؤسسي واستشارات الموارد البشرية والإرشاد المهني.',
      mission: 'مهمتنا',
      missionText: 'تمكين المؤسسات بالمواهب والمهارات التي تحتاجها للازدهار، مع مساعدة الأفراد على تحقيق طموحاتهم المهنية من خلال التطوير المهني والتوجيه الاستراتيجي.',
      vision: 'رؤيتنا',
      visionText: 'أن نكون الشريك الرائد في الموارد البشرية للشركات الدولية التي تبحث عن حلول مواهب عالية الجودة وبرامج تدريبية عالمية المستوى.',
      values: 'قيمنا',
      valuesList: {
        excellence: { title: 'التميز', description: 'تقديم أعلى جودة في كل ما نقوم به' },
        integrity: { title: 'النزاهة', description: 'بناء الثقة من خلال الممارسات الصادقة والأخلاقية' },
        partnership: { title: 'الشراكة', description: 'العمل عن كثب مع العملاء لتحقيق النجاح المشترك' },
        innovation: { title: 'الابتكار', description: 'تبني مناهج جديدة لحل التحديات المعقدة' },
      },
    },
    // CTA Sections
    cta: {
      recruitment: {
        title: 'تبحث عن توظيف؟',
        subtitle: 'دعنا نجد المرشحين المثاليين لفريقك.',
        button: 'طلب دعم التوظيف',
      },
      training: {
        title: 'تحتاج تدريباً مؤسسياً؟',
        subtitle: 'برامج مخصصة مصممة لمؤسستك.',
        button: 'طلب تدريب مخصص',
      },
      jobs: {
        title: 'مستعد لخطوتك المهنية التالية؟',
        subtitle: 'استكشف الفرص مع الشركات الرائدة.',
        button: 'تصفح الوظائف المتاحة',
      },
    },
    // Admin
    admin: {
      dashboard: 'لوحة التحكم',
      jobs: 'الوظائف',
      courses: 'الدورات',
      categories: 'التصنيفات',
      applications: 'الطلبات',
      recruitmentRequests: 'طلبات التوظيف',
      trainingRequests: 'طلبات التدريب',
      inquiries: 'الاستفسارات',
      talentPool: 'مجمع المواهب',
      users: 'المستخدمون',
      settings: 'الإعدادات',
      overview: 'نظرة عامة',
      addNew: 'إضافة جديد',
      edit: 'تعديل',
      delete: 'حذف',
      save: 'حفظ',
      cancel: 'إلغاء',
      status: 'الحالة',
      date: 'التاريخ',
      actions: 'الإجراءات',
      search: 'بحث...',
      noData: 'لا توجد بيانات',
      confirmDelete: 'هل أنت متأكد من حذف هذا العنصر؟',
    },
    // Common
    common: {
      loading: 'جاري التحميل...',
      error: 'حدث خطأ ما',
      back: 'رجوع',
      next: 'التالي',
      previous: 'السابق',
      learnMore: 'اعرف المزيد',
      getStarted: 'ابدأ الآن',
      readMore: 'اقرأ المزيد',
      seeAll: 'عرض الكل',
      close: 'إغلاق',
    },
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('wedo-language');
    return saved || 'en';
  });

  const [direction, setDirection] = useState(() => {
    const saved = localStorage.getItem('wedo-language');
    return saved === 'ar' ? 'rtl' : 'ltr';
  });

  useEffect(() => {
    localStorage.setItem('wedo-language', language);
    const dir = language === 'ar' ? 'rtl' : 'ltr';
    setDirection(dir);
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', language);
  }, [language]);

  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  }, [language]);

  const toggleLanguage = useCallback(() => {
    setLanguage(prev => prev === 'en' ? 'ar' : 'en');
  }, []);

  return (
    <LanguageContext.Provider value={{ language, direction, t, setLanguage, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
